import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API, inr, fdate } from "@/api";
import { Printer, ArrowLeft, Download } from "lucide-react";
import { DynamicFieldsView } from "@/components/CustomView";

export default function InvoicePrint() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    api.get(`/invoices/${id}`).then((r) => setInv(r.data)).catch(() => {});
    api.get("/settings").then((r) => setCompany(r.data)).catch(() => {});
  }, [id]);

  if (!inv || !company)
    return <div className="min-h-screen bg-[#F3F6F5] flex items-center justify-center font-mono2 text-sm text-emerald-600 animate-pulse" data-testid="invoice-print-loading">Loading invoice…</div>;

  const cgst = inv.gst_total / 2;

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="portal-invoice-print-preview">
      <div className="max-w-[820px] mx-auto mb-4 flex items-center justify-between no-print">
        <Link to="/app/invoices" className="inline-flex items-center gap-1.5 text-sm text-slate-700 hover:text-emerald-600 transition-colors duration-200" data-testid="invoice-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to invoices
        </Link>
        <div className="flex items-center gap-2">
          <a href={`${API}/invoices/${inv.invoice_id}/pdf`} download data-testid="invoice-pdf-download-btn"
            className="inline-flex items-center gap-2 rounded-full border border-slate-500 text-slate-800 hover:border-emerald-400 hover:text-emerald-600 font-semibold text-sm px-5 py-2.5 transition-colors duration-200">
            <Download className="w-4 h-4" /> Download PDF
          </a>
          <button onClick={() => window.print()} data-testid="invoice-print-btn"
            className="inline-flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm px-5 py-2.5 transition-colors duration-200">
            <Printer className="w-4 h-4" /> Print / Save as PDF
          </button>
        </div>
      </div>

      <div className="print-page max-w-[820px] mx-auto bg-white text-slate-900 p-8 shadow-xl relative" data-testid="invoice-paper">
        {inv.status === "cancelled" && (
          <>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="text-7xl font-extrabold text-red-500/15 -rotate-12 tracking-widest" data-testid="invoice-cancelled-watermark">CANCELLED</span>
            </div>
            <div className="mb-4 rounded-lg border border-red-300 bg-red-50 text-red-700 text-sm font-semibold px-4 py-2 text-center" data-testid="invoice-cancelled-banner">
              This invoice was cancelled — stock restored and ledger reversed. Not valid for tax credit.
            </div>
          </>
        )}
        {/* Header */}
        <div className="flex items-start justify-between border-b-2 border-emerald-600 pb-5">
          <div className="flex items-start gap-4">
            <img src="/logo.png" alt="GREENRGY" className="h-14" data-testid="invoice-logo" />
            <div>
              <h1 className="text-xl font-bold text-slate-900">{company.company_name}</h1>
              <p className="text-xs text-slate-600 mt-1 max-w-[320px]">{company.address}</p>
              <p className="text-xs text-slate-600">{company.phone} · {company.email}</p>
              <p className="text-xs font-semibold text-slate-800 mt-1">GSTIN: {company.gstin}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-extrabold tracking-tight text-emerald-700">TAX INVOICE</p>
            <p className="text-sm font-mono font-bold mt-1" data-testid="invoice-number">{inv.number}</p>
            <p className="text-xs text-slate-600">Date: {fdate(inv.date)}</p>
            {inv.due_date && <p className="text-xs font-semibold text-amber-700" data-testid="invoice-due-date">Due Date: {fdate(inv.due_date)}{inv.credit_days ? ` (${inv.credit_days}d credit)` : ""}</p>}
            <p className="text-xs text-slate-600">Place of Supply: {inv.client.state || company.state}</p>
          </div>
        </div>

        {/* Buyer */}
        <div className="grid grid-cols-2 gap-6 py-5 border-b border-slate-300">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Billed To</p>
            <p className="font-semibold" data-testid="invoice-buyer-name">{inv.client.name}</p>
            {inv.client.address && <p className="text-xs text-slate-600 mt-0.5">{inv.client.address}</p>}
            {inv.client.state && <p className="text-xs text-slate-600">{inv.client.state}</p>}
            {inv.client.phone && <p className="text-xs text-slate-600">{inv.client.phone}</p>}
            {inv.client.gstin
              ? <p className="text-xs font-semibold mt-1">GSTIN: {inv.client.gstin}</p>
              : <p className="text-xs text-slate-500 mt-1 italic">B2C — Unregistered buyer</p>}
          </div>
          <div className="text-right">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Price Slab Applied</p>
            <span className="inline-block rounded-full border border-emerald-600 text-emerald-700 text-xs font-semibold px-3 py-1 capitalize" data-testid="invoice-slab-tag">
              {inv.slab === "retail" ? "Retail (B2C)" : inv.slab}
            </span>
            {inv.display_custom?.length > 0 && (
              <div className="mt-3 space-y-1" data-testid="invoice-custom-fields">
                {inv.display_custom.map((f, i) => (
                  <p key={i} className="text-xs text-slate-600">
                    <span className="font-semibold text-slate-700">{f.label}:</span> {f.value}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Items */}
        <table className="w-full text-sm mt-5" data-testid="invoice-items-table">
          <thead>
            <tr className="bg-slate-100 text-left text-[11px] uppercase tracking-wider text-slate-600">
              <th className="px-3 py-2">#</th><th className="px-3 py-2">Item</th>
              <th className="px-3 py-2">HSN</th><th className="px-3 py-2 text-right">Qty</th>
              <th className="px-3 py-2 text-right">Rate</th><th className="px-3 py-2 text-right">Taxable</th>
              <th className="px-3 py-2 text-right">GST %</th><th className="px-3 py-2 text-right">GST Amt</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {inv.items.map((it, i) => (
              <tr key={i}>
                <td className="px-3 py-2.5 text-slate-500">{i + 1}</td>
                <td className="px-3 py-2.5 font-medium">{it.name}</td>
                <td className="px-3 py-2.5 font-mono text-xs">{it.hsn || "—"}</td>
                <td className="px-3 py-2.5 text-right">{it.qty} {it.unit}</td>
                <td className="px-3 py-2.5 text-right font-mono">{inr(it.rate)}</td>
                <td className="px-3 py-2.5 text-right font-mono">{inr(it.amount)}</td>
                <td className="px-3 py-2.5 text-right">{it.gst_rate}%</td>
                <td className="px-3 py-2.5 text-right font-mono">{inr(it.gst_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="flex justify-end mt-5">
          <div className="w-80 text-sm space-y-1.5">
            {inv.discount_amount > 0 && (
              <>
                <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span className="font-mono">{inr(inv.subtotal)}</span></div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Less: Discount {inv.discount_type === "percent" ? `(${inv.discount_value}%)` : ""}</span>
                  <span className="font-mono text-red-700 whitespace-nowrap" data-testid="invoice-discount-print">− {inr(inv.discount_amount)}</span>
                </div>
              </>
            )}
            <div className="flex justify-between"><span className="text-slate-600">Taxable Value</span><span className="font-mono" data-testid="invoice-taxable">{inr(inv.taxable ?? inv.subtotal)}</span></div>
            {inv.tax_type === "igst" ? (
              <div className="flex justify-between"><span className="text-slate-600">IGST</span><span className="font-mono">{inr(inv.gst_total)}</span></div>
            ) : (
              <>
                <div className="flex justify-between"><span className="text-slate-600">CGST</span><span className="font-mono">{inr(cgst)}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">SGST</span><span className="font-mono">{inr(cgst)}</span></div>
              </>
            )}
            <div className="flex justify-between border-t-2 border-emerald-600 pt-2 mt-2">
              <span className="font-bold">Grand Total</span>
              <span className="font-mono font-extrabold text-lg text-emerald-700" data-testid="invoice-total">{inr(inv.grand_total)}</span>
            </div>
            {inv.paid_amount > 0 && (
              <>
                <div className="flex justify-between">
                  <span className="text-slate-600 whitespace-nowrap">Less: Advance / On-account Payment</span>
                  <span className="font-mono text-blue-700 whitespace-nowrap" data-testid="invoice-advance-paid">− {inr(inv.paid_amount)}</span>
                </div>
                <div className="flex justify-between border-t border-slate-300 pt-1.5">
                  <span className="font-bold">Balance Due</span>
                  <span className={`font-mono font-extrabold ${inv.balance_due > 0 ? "text-amber-700" : "text-emerald-700"}`} data-testid="invoice-balance-due">{inr(inv.balance_due)}</span>
                </div>
              </>
            )}
            {inv.final_dues !== undefined && inv.final_dues !== null && (
              <>
                {inv.previous_dues > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-600 whitespace-nowrap">Add: Previous / Old Dues</span>
                    <span className="font-mono text-amber-700 whitespace-nowrap" data-testid="invoice-previous-dues">+ {inr(inv.previous_dues)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t-2 border-slate-700 pt-1.5 mt-1">
                  <span className="font-bold">Final Dues (Total Outstanding)</span>
                  <span className={`font-mono font-extrabold text-lg ${inv.final_dues > 0 ? "text-amber-700" : "text-emerald-700"}`} data-testid="invoice-final-dues">{inr(inv.final_dues)}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {inv.bank_details && (
          <div className="mt-5 max-w-md rounded-lg border border-slate-300 bg-slate-50 px-4 py-3" data-testid="invoice-bank-details">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Bank Details</p>
            <p className="text-sm font-semibold text-slate-900">{inv.bank_details.name}{inv.bank_details.bank ? ` · ${inv.bank_details.bank}` : ""}</p>
            <p className="text-xs font-mono text-slate-700">
              {inv.bank_details.account_number && <>A/c No: <b>{inv.bank_details.account_number}</b></>}
              {inv.bank_details.ifsc && <> · IFSC: <b>{inv.bank_details.ifsc}</b></>}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-10 pt-5 border-t border-slate-300 flex items-end justify-between">
          <div className="text-xs text-slate-500 max-w-[380px]">
            <p className="font-semibold text-slate-700 mb-1">Terms & Declaration</p>
            <p>{company.invoice_footer}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-slate-500 mb-8">For {company.company_name}</p>
            <p className="text-xs font-semibold border-t border-slate-400 pt-1.5 px-6">Authorised Signatory</p>
          </div>
        </div>
      </div>
    </div>
  );
}
